import { useState, useCallback } from 'react'
import { omit } from 'lodash'

export interface ISelection {
  items: IDHash
  toggle: (id: string) => void
  add: (id: string) => void
  addAll: (ids: string[]) => void
  clear: () => void
  count: number
  hasAllItemsSelected: (itemsLength: number) => boolean
  remove: (ids: string[]) => void
  removeSelectionsByItems: <ItemT extends { id: string }>(ids: ItemT[]) => void
}

interface IDHash {
  [id: string]: boolean
}

export default function useSelection(): ISelection {
  const [items, setSelected] = useState<IDHash>({})
  const count = Object.keys(items).length

  const toggle = useCallback(
    (id: string) => {
      const sel = { ...items }
      if (sel[id]) delete sel[id]
      else sel[id] = true
      setSelected(sel)
    },
    [items]
  )

  const remove = useCallback(
    (ids: string[]) => {
      setSelected({ ...omit(items, ids) })
    },
    [items]
  )

  const add = useCallback((id: any) => setSelected({ ...items, [id]: true }), [items])

  const addAll = useCallback(
    (ids: any) => {
      const newItems = ids.reduce(
        (result: IDHash, id: string) => ({
          ...result,
          [id]: true,
        }),
        {}
      )
      setSelected({ ...items, ...newItems })
    },
    [items]
  )

  const clear = useCallback(() => setSelected({}), [])

  const removeSelectionsByItems: ISelection['removeSelectionsByItems'] = (remainingItems) => {
    const selectionItemsForRemove: string[] = []

    //clear selection outside filtered range
    Object.keys(items).forEach((selectionItem) => {
      const exist = remainingItems.find((i) => i.id === selectionItem)

      if (!exist) {
        selectionItemsForRemove.push(selectionItem)
      }
    })

    if (selectionItemsForRemove.length !== 0) {
      remove(selectionItemsForRemove)
    }
  }

  const hasAllItemsSelected = useCallback(
    (itemsLength: number) => count > 0 && count === itemsLength,
    [count]
  )

  return {
    items,
    add,
    addAll,
    clear,
    toggle,
    remove,
    count,
    hasAllItemsSelected,
    removeSelectionsByItems,
  }
}
